import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import { Checkbox, Label } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

export interface MultiSelectIdentificationTokenOriginProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedOrigins?: string[];
  onChange: (selectedOrigins: string[]) => void;
}

const MultiSelectIdentificationTokenOrigin = ({ isOpen, size = 'md', selectedOrigins = [], onChange }: MultiSelectIdentificationTokenOriginProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedOrigins);

  const hasSameElements = selectedOrigins?.length === draft?.length && selectedOrigins.every((value) => draft.includes(value));

  const options = [
    { value: 'COBLI', label: t('identification_token_origin.cobli') },
    { value: 'MANUAL', label: t('identification_token_origin.manual') },
    { value: 'SPREADSHEET', label: t('identification_token_origin.spreadsheet') },
  ];

  const onDropdownOpen = useCallback(() => {
    setDraft(selectedOrigins);
  }, [selectedOrigins]);

  const onChangeSelect = (value: string) => {
    const isSelected = draft.includes(value);
    setDraft(isSelected ? draft.filter((d) => d !== value) : [...draft, value]);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft(selectedOrigins);
  }, [selectedOrigins]);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      portal={false}
      onOpenChange={onDropdownOpen}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              {t('identification_token_origin.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={150}
              heightItem={28}
              data={options}
              itemComponent={(item) => (
                <div className='flex items-center gap-2'>
                  <Checkbox
                    id={`token-origin-${item.value}`}
                    key={`token-origin-${item.value}`}
                    checked={draft.includes(item.value)}
                    onCheckedChange={() => onChangeSelect(item.value)}
                    data-testid={`checkbox-identification-token-origin-${item.value}`}
                  />
                  <Label htmlFor={`token-origin-${item.value}`}>{item.label}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('identification_token_origin.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('identification_token_origin.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='building-2' />}>
        {t('identification_token_origin.title')} {(selectedOrigins?.length || 0) > 0 ? `(${selectedOrigins?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectIdentificationTokenOrigin;
